import { useContext, useEffect, useState } from 'react';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Filtros } from './Filtros';
import { ActionsTable } from './components/ActionsTable/ActionsTable';
import { BtnIniciarPedido } from './components/BtnPedido/BtnIniciarPedido';
import { Loading } from './components/Loading/Loading';
import { Grupos } from './Grupos';
import { useGlobalStore } from '../../Hooks/useGlobalStore';
import { useURLParams } from './hooks/useURLParams';
import { useCatalog } from './hooks/useCatalog';
import { useClient } from './hooks/useClient';
import { PRICE_WITH_IVA, useUserConfig } from '../../Hooks/useUserConfig';
import { Switch } from '../Switch/Switch';
import { UserContext } from '../auth/context/UserContext';
import { GroupedProducts } from './components/Catalog/Groups/GroupedProducts/GroupedProducts';
import { UngroupedProducts } from './components/Catalog/Groups/UngroupedProducts/UngroupedProducts';
import { useSearchParams } from 'react-router-dom';
import Cookies from 'universal-cookie';

export const Catalogo = () => {
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const { searchParams, updateParms } = useURLParams();

  const gruposParam = urlSearchParams.get('grupos');
  const [pagina, setPagina] = useState(1);
  const [filterSearch, setFilterSearch] = useState(() => {
    const paramSearch = searchParams.get('denominacion');
    return paramSearch ? paramSearch : '';
  });
  const [showGrupos, setShowGrupos] = useState(false);
  const [visiblefiltros, setVisibleFiltros] = useState(false);
  const { data: isMobile } = useGlobalStore('isMobile');
  const { data: dataCatalog, isFetching } = useCatalog();
  const [totalPaginas, setTotalPaginas] = useState(dataCatalog?.paginas || 0);
  const { data: dataClient } = useClient();
  const { setPriceWithIva, userConfig: priceWithIva = false } =
    useUserConfig(PRICE_WITH_IVA);
  const { dataUser } = useContext(UserContext);
  const userData = dataUser;
  const [selectedGroups, setSelectedGroups] = useState(new Map());
  const [groupedProducts, setGroupedProducts] = useState({});
  const [hasSavedGroups, setHasSavedGroups] = useState(false);

  // Función auxiliar para obtener datos de grupo por ID (simulada)
  const getGroupDataById = (id) => {
    // Implementa esta función según tu estructura de datos
    return { id, type: 'Rubro', denomination: `Grupo ${id}` };
  };

  useEffect(() => {
    setTotalPaginas(dataCatalog?.paginas);
  }, [dataCatalog]);

  useEffect(() => {
    const paramPage = searchParams.get('pagina') || 1;
    const res = paramPage ? Number(paramPage) : 1;
    setPagina(res <= totalPaginas ? res : totalPaginas);
  }, [searchParams, totalPaginas]);

  useEffect(() => {
    if (userData?.clienteId && !priceWithIva) {
      setPriceWithIva(true);
    }
  }, [userData?.clienteId, priceWithIva, setPriceWithIva]);

  // Efecto principal para manejar grupos desde URL o cookies
  useEffect(() => {
    const cookies = new Cookies();
    const filterParam = searchParams.get('filter');
    const searchParam = searchParams.get('denominacion');

    if (filterParam || searchParam) {
      setSelectedGroups(new Map());
      setGroupedProducts({});
      setHasSavedGroups(false);
      cookies.remove('grupos');
      return;
    }

    // 1. Prioridad a los grupos de la URL
    if (gruposParam) {
      const gruposIds = gruposParam.split(',');
      const groupsMap = new Map();

      gruposIds.forEach((id) => {
        const groupData = getGroupDataById(id);
        if (groupData) {
          groupsMap.set(id, groupData);
        }
      });

      setSelectedGroups(groupsMap);
      updateGroupedProducts(groupsMap, dataCatalog?.data);
      setHasSavedGroups(groupsMap.size > 0);

      // Persistir en cookies
      cookies.set('grupos', Array.from(groupsMap.entries()), { path: '/' });
    }
    // 2. Si no hay en URL, verificar cookies
    else {
      const savedGroups = cookies.get('grupos');
      if (savedGroups) {
        try {
          const groupsMap = new Map(savedGroups);
          setSelectedGroups(groupsMap);
          updateGroupedProducts(groupsMap, dataCatalog?.data);
          setHasSavedGroups(true);

          // Actualizar URL con los grupos de las cookies
          const gruposIds = Array.from(groupsMap.keys()).join(',');
          urlSearchParams.set('grupos', gruposIds);
          setUrlSearchParams(urlSearchParams);
        } catch (e) {
          console.error('Error loading saved groups:', e);
        }
      }
    }
  }, [gruposParam, dataCatalog?.data]);

  const getUngroupedProducts = (allProducts = [], groupedProducts = {}) => {
    const groupedIds = new Set();

    Object.values(groupedProducts).forEach((group) => {
      group?.forEach((product) => {
        if (product?.id) groupedIds.add(product.id);
      });
    });

    return (
      allProducts?.filter(
        (product) => product && !groupedIds.has(product.id)
      ) || []
    );
  };

  const updateGroupedProducts = (groups, products) => {
    if (!products) return;

    const newGroupedProducts = {};
    groups.forEach((groupData, groupId) => {
      const groupKey = `${groupData.type}: ${groupData.denomination}`;
      newGroupedProducts[groupKey] = products.filter((product) =>
        product.groups?.some((g) => g.id === groupId)
      );
    });

    setGroupedProducts(newGroupedProducts);
  };

  const handleProductClick = (product) => {
    if (!product?.id || selectedGroups.size === 0) return;

    const groupKey = Array.from(selectedGroups)
      .map(([id, groupData]) => {
        const productGroupValue =
          product.groups?.find((g) => g.id === id)?.value ||
          groupData.denomination;
        return `${groupData.type}: ${productGroupValue}`;
      })
      .join(' | ');

    setGroupedProducts((prev) => {
      const newGrouped = { ...prev };
      Object.keys(newGrouped).forEach((key) => {
        newGrouped[key] =
          newGrouped[key]?.filter((p) => p.id !== product.id) || [];
      });

      newGrouped[groupKey] = [...(newGrouped[groupKey] || []), product];
      return newGrouped;
    });
  };

  const clearAllGroups = () => {
    setSelectedGroups(new Map());
    setGroupedProducts({});
    setHasSavedGroups(false);

    const cookies = new Cookies();
    cookies.remove('grupos');

    urlSearchParams.delete('grupos');
    setUrlSearchParams(urlSearchParams);
  };

  const updatePage = (page) => {
    setPagina(page);
    updateParms(['pagina', page]);
  };

  const handleSearchInputChange = ({ target }) => {
    const value = target.value.toUpperCase();
    setFilterSearch(value);
    setPagina(1);

    setSelectedGroups(new Map());
    setGroupedProducts({});
    setHasSavedGroups(false);

    updateParms(['denominacion', value, 'pagina', 1]);
  };

  const handleGroupSelection = (selectedGroups) => {
    setSelectedGroups(selectedGroups);
    updateGroupedProducts(selectedGroups, dataCatalog?.data);

    const cookies = new Cookies();
    const gruposIds = Array.from(selectedGroups.keys()).join(',');

    if (selectedGroups.size > 0) {
      setHasSavedGroups(true);
      cookies.set('grupos', Array.from(selectedGroups.entries()), {
        path: '/',
      });
      urlSearchParams.set('grupos', gruposIds);
    } else {
      setHasSavedGroups(false);
      cookies.remove('grupos');
      urlSearchParams.delete('grupos');
    }

    setUrlSearchParams(urlSearchParams);
  };

  useEffect(() => {
    const cookies = new Cookies();
    const urlParams = new URLSearchParams(window.location.search);
    const filterParam = urlParams.get('filter');
    const searchParam = urlParams.get('denominacion');

    const savedGroups = cookies.get('grupos');

    if (filterParam || searchParam) {
      setSelectedGroups(new Map());
      setGroupedProducts({});
      setHasSavedGroups(false);
      return;
    }

    if (savedGroups) {
      try {
        const groupsMap = new Map(savedGroups);
        setSelectedGroups(groupsMap);
        updateGroupedProducts(groupsMap, dataCatalog?.data);

        const gruposIds = Array.from(groupsMap.keys()).join(',');
        //updateParms(['grupos', gruposIds, 'pagina', 1]);
        setHasSavedGroups(true);
      } catch (e) {
        console.error('Error loading saved groups:', e);
      }
    } else {
      setHasSavedGroups(false);
    }
  }, [dataCatalog?.data]);

  const sumaPagina = () => pagina < totalPaginas && updatePage(pagina + 1);
  const restaPagina = () => pagina > 1 && updatePage(pagina - 1);
  const toggleFiltros = () => setVisibleFiltros(!visiblefiltros);
  const toggleGrupos = () => setShowGrupos(!showGrupos);

  const productsToDisplay = getUngroupedProducts(
    dataCatalog?.data,
    groupedProducts
  );

  return (
    <div className="grandcontainer__catalogo">
      <div className="encabezado_catalogo">
        <Filtros visible={visiblefiltros} toggleFiltros={toggleFiltros} />
        <Grupos
          visible={showGrupos}
          toggleGrupos={toggleGrupos}
          onGroupSelection={handleGroupSelection}
          initialSelectedGroups={selectedGroups}
        />
        <div className="search-container">
          <BtnIniciarPedido />
          <input
            type="text"
            id="search-input"
            className="search-input"
            placeholder="Buscar..."
            value={filterSearch}
            onChange={handleSearchInputChange}
          />

          <div className="contenedor-icons" style={{ display: 'flex' }}>
            <div className="tooltip">
              <span className="tooltiptext">Precios con IVA</span>
              {!userData?.clienteId && (
                <Switch
                  id="withIva"
                  type="checkbox"
                  checked={priceWithIva}
                  onChange={setPriceWithIva}
                  className="price-with-iva"
                ></Switch>
              )}
            </div>
            <ActionsTable
              toggleFiltros={toggleFiltros}
              toggleGrupos={toggleGrupos}
            />
          </div>
        </div>
      </div>

      <div className="catalogosencillo">
        {isFetching ? (
          <Loading enabled />
        ) : (
          <>
            {hasSavedGroups ? (
              <GroupedProducts
                groupedProducts={groupedProducts}
                isMobile={isMobile}
                dataClient={dataClient}
                products={productsToDisplay}
                onProductClick={handleProductClick}
                onClearAll={clearAllGroups}
              />
            ) : (
              <UngroupedProducts
                products={productsToDisplay}
                isMobile={isMobile}
                dataClient={dataClient}
                onProductClick={handleProductClick}
                hasGroups={false}
              />
            )}
            {!isFetching && !hasSavedGroups && (
              <div className="paginacion">
                <button className="paginacion-antes" onClick={restaPagina}>
                  <FontAwesomeIcon icon={faChevronLeft} className="iconleft" />
                  Anterior
                </button>
                <span className="paginacion-centro">
                  {pagina} de {totalPaginas}
                </span>
                <button className="paginacion-despues" onClick={sumaPagina}>
                  Siguiente
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    className="iconright"
                  />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Catalogo;

// import { useContext, useEffect, useState } from 'react';
// import {
//   faChevronLeft,
//   faChevronRight,
// } from '@fortawesome/free-solid-svg-icons';
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import { Filtros } from './Filtros';
// import { ActionsTable } from './components/ActionsTable/ActionsTable';
// import { BtnIniciarPedido } from './components/BtnPedido/BtnIniciarPedido';
// import { Loading } from './components/Loading/Loading';
// import { Grupos } from './Grupos';
// import { useGlobalStore } from '../../Hooks/useGlobalStore';
// import { useURLParams } from './hooks/useURLParams';
// import { useCatalog } from './hooks/useCatalog';
// import { useClient } from './hooks/useClient';
// import { PRICE_WITH_IVA, useUserConfig } from '../../Hooks/useUserConfig';
// import { Switch } from '../Switch/Switch';
// import { UserContext } from '../auth/context/UserContext';
// import { GroupedProducts } from './components/Catalog/Groups/GroupedProducts/GroupedProducts';

// import { UngroupedProducts } from './components/Catalog/Groups/UngroupedProducts/UngroupedProducts';

// import Cookies from 'universal-cookie';

// export const Catalogo = () => {
//   const { searchParams, updateParms } = useURLParams();
//   const [pagina, setPagina] = useState(1);
//   const [filterSearch, setFilterSearch] = useState(() => {
//     const paramSearch = searchParams.get('denominacion');
//     return paramSearch ? paramSearch : '';
//   });
//   const [showGrupos, setShowGrupos] = useState(false);
//   const [visiblefiltros, setVisibleFiltros] = useState(false);
//   const { data: isMobile } = useGlobalStore('isMobile');
//   const { data: dataCatalog, isFetching } = useCatalog();
//   const [totalPaginas, setTotalPaginas] = useState(dataCatalog?.paginas || 0);
//   const { data: dataClient } = useClient();
//   const { setPriceWithIva, userConfig: priceWithIva = false } =
//     useUserConfig(PRICE_WITH_IVA);
//   const { dataUser } = useContext(UserContext);
//   const userData = dataUser;
//   const [selectedGroups, setSelectedGroups] = useState(new Map());
//   const [groupedProducts, setGroupedProducts] = useState({});

//   useEffect(() => setTotalPaginas(dataCatalog?.paginas), [dataCatalog]);

//   useEffect(() => {
//     const paramPage = searchParams.get('pagina') || 1;
//     const res = paramPage ? Number(paramPage) : 1;
//     setPagina(res <= totalPaginas ? res : totalPaginas);
//   }, [searchParams, totalPaginas]);

//   useEffect(() => {
//     if (userData?.clienteId && !priceWithIva) {
//       setPriceWithIva(true);
//     }
//   }, [userData?.clienteId, priceWithIva, setPriceWithIva]);

//   const getUngroupedProducts = (allProducts = [], groupedProducts = {}) => {
//     const groupedIds = new Set();

//     Object.values(groupedProducts).forEach((group) => {
//       group?.forEach((product) => {
//         if (product?.id) groupedIds.add(product.id);
//       });
//     });

//     return (
//       allProducts?.filter(
//         (product) => product && !groupedIds.has(product.id)
//       ) || []
//     );
//   };

//   const updateGroupedProducts = (groups, products) => {
//     if (!products) return;

//     const newGroupedProducts = {};
//     groups.forEach((groupData, groupId) => {
//       const groupKey = `${groupData.type}: ${groupData.denomination}`;
//       newGroupedProducts[groupKey] = products.filter((product) =>
//         product.groups?.some((g) => g.id === groupId)
//       );
//     });

//     setGroupedProducts(newGroupedProducts);
//   };

//   const handleProductClick = (product) => {
//     if (!product?.id || selectedGroups.size === 0) return;

//     const groupKey = Array.from(selectedGroups)
//       .map(([id, groupData]) => {
//         const productGroupValue =
//           product.groups?.find((g) => g.id === id)?.value ||
//           groupData.denomination;
//         return `${groupData.type}: ${productGroupValue}`;
//       })
//       .join(' | ');

//     setGroupedProducts((prev) => {
//       const newGrouped = { ...prev };
//       Object.keys(newGrouped).forEach((key) => {
//         newGrouped[key] =
//           newGrouped[key]?.filter((p) => p.id !== product.id) || [];
//       });

//       newGrouped[groupKey] = [...(newGrouped[groupKey] || []), product];
//       return newGrouped;
//     });
//   };

//   const clearAllGroups = () => setGroupedProducts({});

//   const updatePage = (page) => {
//     setPagina(page);
//     updateParms(['pagina', page]);
//   };

//   const handleSearchInputChange = ({ target }) => {
//     const value = target.value.toUpperCase();
//     setFilterSearch(value);
//     setPagina(1);

//     setSelectedGroups(new Map());
//     setGroupedProducts({});
//     setHasSavedGroups(false);

//     updateParms(['denominacion', value, 'pagina', 1]);
//   };

//   const sumaPagina = () => pagina < totalPaginas && updatePage(pagina + 1);
//   const restaPagina = () => pagina > 1 && updatePage(pagina - 1);
//   const toggleFiltros = () => setVisibleFiltros(!visiblefiltros);
//   const toggleGrupos = () => setShowGrupos(!showGrupos);

//   const productsToDisplay = getUngroupedProducts(
//     dataCatalog?.data,
//     groupedProducts
//   );

//   const handleGroupSelection = (selectedGroups) => {
//     setSelectedGroups(selectedGroups);

//     const newGroupedProducts = {};

//     selectedGroups.forEach((groupData, groupId) => {
//       const groupKey = `${groupData.type}: ${groupData.denomination}`;

//       dataCatalog?.data?.forEach((product) => {
//         if (product.groups?.some((g) => g.id === groupId)) {
//           if (!newGroupedProducts[groupKey]) {
//             newGroupedProducts[groupKey] = [];
//           }
//           newGroupedProducts[groupKey].push(product);
//         }
//       });
//     });

//     setGroupedProducts(newGroupedProducts);
//   };
//   const [hasSavedGroups, setHasSavedGroups] = useState(false);

//   useEffect(() => {
//     const cookies = new Cookies();
//     const urlParams = new URLSearchParams(window.location.search);
//     const filterParam = urlParams.get('filter');
//     const searchParam = urlParams.get('denominacion');

//     const savedGroups = cookies.get('grupos');

//     if (filterParam || searchParam) {
//       setSelectedGroups(new Map());
//       setGroupedProducts({});
//       setHasSavedGroups(false);
//       return;
//     }

//     if (savedGroups) {
//       try {
//         const groupsMap = new Map(savedGroups);
//         setSelectedGroups(groupsMap);
//         updateGroupedProducts(groupsMap, dataCatalog?.data);

//         const gruposIds = Array.from(groupsMap.keys()).join(',');
//         //updateParms(['grupos', gruposIds, 'pagina', 1]);
//         setHasSavedGroups(true);
//       } catch (e) {
//         console.error('Error loading saved groups:', e);
//       }
//     } else {
//       setHasSavedGroups(false);
//     }
//   }, [dataCatalog?.data]);

//   return (
//     <div className="grandcontainer__catalogo">
//       {/* Encabezado */}
//       <div className="encabezado_catalogo">
//         <Filtros visible={visiblefiltros} toggleFiltros={toggleFiltros} />
//         <Grupos
//           visible={showGrupos}
//           toggleGrupos={toggleGrupos}
//           onGroupSelection={handleGroupSelection}
//         />
//         <div className="search-container">
//           <BtnIniciarPedido />
//           <input
//             type="text"
//             id="search-input"
//             className="search-input"
//             placeholder="Buscar..."
//             value={filterSearch}
//             onChange={handleSearchInputChange}
//           />

//           <div className="contenedor-icons" style={{ display: 'flex' }}>
//             <div className="tooltip">
//               <span className="tooltiptext">Precios con IVA</span>
//               {!userData?.clienteId && (
//                 <Switch
//                   id="withIva"
//                   type="checkbox"
//                   checked={priceWithIva}
//                   onChange={setPriceWithIva}
//                   className="price-with-iva"
//                 ></Switch>
//               )}
//             </div>
//             <ActionsTable
//               toggleFiltros={toggleFiltros}
//               toggleGrupos={toggleGrupos}
//             />
//           </div>
//         </div>
//       </div>

//       <div className="catalogosencillo">
//         {isFetching ? (
//           <Loading enabled />
//         ) : (
//           <>
//             {hasSavedGroups ? (
//               <GroupedProducts
//                 groupedProducts={groupedProducts}
//                 isMobile={isMobile}
//                 dataClient={dataClient}
//                 products={productsToDisplay}
//                 onProductClick={handleProductClick}
//                 onClearAll={clearAllGroups}
//               />
//             ) : (
//               <UngroupedProducts
//                 products={productsToDisplay}
//                 isMobile={isMobile}
//                 dataClient={dataClient}
//                 onProductClick={handleProductClick}
//                 hasGroups={false}
//               />
//             )}
//             {!isFetching && !hasSavedGroups && (
//               <div className="paginacion">
//                 <button className="paginacion-antes" onClick={restaPagina}>
//                   <FontAwesomeIcon icon={faChevronLeft} className="iconleft" />
//                   Anterior
//                 </button>
//                 <span className="paginacion-centro">
//                   {pagina} de {totalPaginas}
//                 </span>
//                 <button className="paginacion-despues" onClick={sumaPagina}>
//                   Siguiente
//                   <FontAwesomeIcon
//                     icon={faChevronRight}
//                     className="iconright"
//                   />
//                 </button>
//               </div>
//             )}
//           </>
//         )}
//       </div>
//     </div>
//   );
// };

// export default Catalogo;
